/**
 * Add Note Modal Component
 * Modal for adding notes to a lead
 */

import React, { useState } from 'react';
import { X, MessageSquare, StickyNote } from 'lucide-react';

const NOTE_TYPES = [
  { id: 'general', label: 'General Note', description: 'General information about the lead' },
  { id: 'follow_up', label: 'Follow-up', description: 'Follow-up action item' },
  { id: 'call_notes', label: 'Call Notes', description: 'Notes from a phone call' },
  { id: 'meeting_notes', label: 'Meeting Notes', description: 'Notes from an in-person meeting' },
];

const AddNoteModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [noteType, setNoteType] = useState('general');
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isPrivate: false,
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const data = {
      type: 'note',
      title: formData.title || getDefaultTitle(noteType),
      content: formData.content,
      isPrivate: formData.isPrivate,
      noteType: noteType,
    };

    onSubmit(data);
  };

  const getDefaultTitle = (type) => {
    const titles = {
      general: 'Note added',
      follow_up: 'Follow-up note',
      call_notes: 'Call notes',
      meeting_notes: 'Meeting notes',
    };
    return titles[type] || 'Note added';
  };

  const handleClose = () => {
    setNoteType('general');
    setFormData({
      title: '',
      content: '',
      isPrivate: false,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <StickyNote size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add Note</h2>
              <p className="text-sm text-gray-500">Add a note to this lead</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <form id="note-form" onSubmit={handleSubmit} className="space-y-6">
            {/* Note Type Selection */}
            <div>
              <label className="form-label">Note Type</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {NOTE_TYPES.map((type) => {
                  const Icon = MessageSquare;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setNoteType(type.id)}
                      className={`flex items-start p-3 rounded-lg border text-left transition-all ${
                        noteType === type.id
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                      }`}
                    >
                      <Icon size={18} className="mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-sm">{type.label}</span>
                        <p className="text-xs mt-0.5 opacity-75">{type.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="form-label">
                Title <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="text"
                className="form-input"
                placeholder={getDefaultTitle(noteType)}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              />
            </div>

            {/* Content */}
            <div>
              <label className="form-label">Note Content *</label>
              <textarea
                className="form-input"
                rows="5"
                placeholder="Enter your note..."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </div>

            {/* Privacy toggle */}
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={formData.isPrivate}
                onChange={(e) => setFormData({ ...formData, isPrivate: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <span className="text-gray-700">Make this note private (admin only)</span>
            </label>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="note-form"
            disabled={isLoading || !formData.content.trim()}
            className="btn-primary disabled:opacity-50"
          >
            {isLoading ? 'Adding...' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddNoteModal;
